import { useRouter } from "@uirouter/react";
import { noop } from "lodash";
import { FunctionComponent, useCallback, useContext } from "react";
import { useQuery } from "react-query";
import { match } from "ts-pattern";
import { Button } from "../../../../../components/button";
import { Icon } from "../../../../../components/icons";
import { Flex } from "../../../../../components/layout";
import { useAppLoader } from "../../../../../components/loader/app-loader-hook";
import { Spinner } from "../../../../../components/loader/icon";
import { GlobalOverlayContext } from "../../../../../components/overlay/context";
import { useDefaultQueryOptions } from "../../../../../store/client-query";
import { ALL_SUBMERCHANTS } from "../../../../../store/merchants/resources";
import { UsersDTO } from "../../../../../store/users/models/dto";
import { UsersService } from "../../../../../store/users/service";
import { PresentationalMerchantsList } from "../../../../merchants/all/presentational";
import { NewMerchant } from "../../../../merchants/new/inner-component";
import { PasswordGeneratedModal } from "../../../../merchants/new/password-generated";
import { StyledWidget } from "../../common/components";

type CreateMerchant = UsersDTO.Requests.Merchant.Create.T;

export const SubMerchants: FunctionComponent<{
  item: UsersDTO.Responses.GetSingle.T;
}> = ({ item }) => {
  const { userReferenceId } = item;
  const { setOverlay } = useContext(GlobalOverlayContext);

  const resource = useQuery(
    [ALL_SUBMERCHANTS, userReferenceId],
    ({ queryKey }) =>
      UsersService.getAllSubMerchants(queryKey[1], {
        pagination: { page: 0, size: 1000 },
      }),
    useDefaultQueryOptions()
  );

  const loader = useAppLoader();
  const router = useRouter();

  const onCreateMerchant = useCallback(
    (x: CreateMerchant) =>
      loader(UsersService.createSubmerchant(item.userReferenceId, x))
        .then((res) =>
          router.stateService.reload().finally(() =>
            setOverlay({
              title: "Sub Merchant Password",
              children: <PasswordGeneratedModal {...res} />,
            })
          )
        )
        .catch(noop),
    [item.userReferenceId, loader, router]
  );

  const openModal = useCallback(
    () =>
      setOverlay({
        title: "Create Sub Merchant",
        children: <NewMerchant onSubmit={onCreateMerchant} />,
      }),
    [setOverlay, item.userReferenceId, onCreateMerchant]
  );

  return (
    <StyledWidget
      title="Sub Merchants"
      adornment={
        <Button onClick={openModal} variant="contained">
          <Icon type="plus" />
        </Button>
      }
    >
      {match(resource)
        .with({ status: "loading" }, () => (
          <Flex center>
            <Spinner />
          </Flex>
        ))
        .with(
          {
            status: "success",
          },
          ({ data }) => <PresentationalMerchantsList usersList={data} />
        )
        .otherwise(() => null)}
    </StyledWidget>
  );
};
